import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Sun, CloudSun, Cloud, CloudFog, CloudDrizzle, CloudRain, CloudRainWind,
    CloudSnow, CloudLightning, Droplets, Wind, Thermometer, RefreshCw, MapPin, CloudOff,
    Gauge, Sunrise, Sunset, ShieldAlert, Info,
} from 'lucide-react';
import {
    ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, ReferenceArea,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

const POLL_MS = 10 * 60 * 1000; // 10 min — la météo ne bouge pas vite

// ── WMO weather code → label FR + icône + couleur ──────────────────────────────
function wmo(code) {
    const M = {
        0:  ['Ciel clair', Sun, 'text-amber-400'],
        1:  ['Principalement clair', CloudSun, 'text-amber-400'],
        2:  ['Partiellement nuageux', CloudSun, 'text-slate-400'],
        3:  ['Couvert', Cloud, 'text-slate-400'],
        45: ['Brouillard', CloudFog, 'text-slate-400'],
        48: ['Brouillard givrant', CloudFog, 'text-slate-400'],
        51: ['Bruine légère', CloudDrizzle, 'text-blue-400'],
        53: ['Bruine', CloudDrizzle, 'text-blue-400'],
        55: ['Bruine dense', CloudDrizzle, 'text-blue-400'],
        56: ['Bruine verglaçante', CloudDrizzle, 'text-blue-400'],
        57: ['Bruine verglaçante', CloudDrizzle, 'text-blue-400'],
        61: ['Pluie faible', CloudRain, 'text-blue-400'],
        63: ['Pluie modérée', CloudRain, 'text-blue-500'],
        65: ['Pluie forte', CloudRainWind, 'text-blue-500'],
        66: ['Pluie verglaçante', CloudRain, 'text-blue-400'],
        67: ['Pluie verglaçante', CloudRain, 'text-blue-500'],
        71: ['Neige faible', CloudSnow, 'text-sky-300'],
        73: ['Neige', CloudSnow, 'text-sky-300'],
        75: ['Neige forte', CloudSnow, 'text-sky-300'],
        77: ['Grains de neige', CloudSnow, 'text-sky-300'],
        80: ['Averses faibles', CloudRain, 'text-blue-400'],
        81: ['Averses', CloudRain, 'text-blue-500'],
        82: ['Averses violentes', CloudRainWind, 'text-blue-600'],
        85: ['Averses de neige', CloudSnow, 'text-sky-300'],
        86: ['Averses de neige', CloudSnow, 'text-sky-300'],
        95: ['Orage', CloudLightning, 'text-violet-400'],
        96: ['Orage, grêle', CloudLightning, 'text-violet-400'],
        99: ['Orage, grêle', CloudLightning, 'text-violet-500'],
    };
    return M[code] ?? ['—', Cloud, 'text-slate-400'];
}

function uvLevel(v) {
    if (v === null || v === undefined) return null;
    if (v < 3)  return { label: 'Faible',      cls: 'text-green-500 bg-green-500/10' };
    if (v < 6)  return { label: 'Modéré',       cls: 'text-yellow-500 bg-yellow-500/10' };
    if (v < 8)  return { label: 'Élevé',        cls: 'text-orange-500 bg-orange-500/10' };
    if (v < 11) return { label: 'Très élevé',   cls: 'text-red-500 bg-red-500/10' };
    return          { label: 'Extrême',     cls: 'text-purple-500 bg-purple-500/10' };
}

const n1  = (v, s = '') => (v === null || v === undefined ? '--' : `${Math.round(v)}${s}`);
const n1d = (v, s = '') => (v === null || v === undefined ? '--' : `${Number(v).toFixed(1)}${s}`);

function dayLabel(dateStr, idx) {
    if (idx === 0) return 'Aujourd\'hui';
    try {
        const s = new Date(dateStr).toLocaleDateString('fr-FR', { weekday: 'short' }).replace('.', '');
        return s.charAt(0).toUpperCase() + s.slice(1);
    } catch { return dateStr; }
}
function hourLabel(t) {
    const h = t?.slice(11, 13);
    return h ? `${parseInt(h, 10)}h` : '';
}
function timeLabel(t) {
    if (!t) return '--';
    const h = t.slice(11, 16);
    return h || '--';
}

// ── StatTile ─────────────────────────────────────────────────────────────────

function StatTile({ icon: StatIcon, label, value, sub, iconCls = 'text-muted-foreground' }) {
    return (
        <div className="bg-muted/40 rounded-lg py-2.5 px-2 text-center">
            <p className="text-[10px] text-muted-foreground flex items-center justify-center gap-1">
                <StatIcon className={`w-3 h-3 ${iconCls}`} />{label}
            </p>
            <p className="text-sm font-semibold text-foreground mt-1">{value}</p>
            {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
        </div>
    );
}

// ── Hourly chart tooltip — declared outside HourlyChart so it isn't recreated every render ──

function HourlyTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload;
    return (
        <div className="bg-card border border-border rounded-lg px-3 py-2 text-xs shadow-lg">
            <p className="font-semibold text-foreground mb-1">{hourLabel(label)}{row?.hd ? ' · AROME HD' : ''}</p>
            <p className="text-foreground">{n1(row?.temp, '°C')}</p>
            <p className="text-blue-400">{n1d(row?.precip, ' mm')}{row?.precip_prob != null ? ` · ${row.precip_prob}%` : ''}</p>
        </div>
    );
}

// ── Hourly chart (temp + pluie, 48h, zone AROME HD) ───────────────────────────

function HourlyChart({ hourly }) {
    if (!hourly || hourly.length === 0)
        return <div className="h-40 flex items-center justify-center text-muted-foreground text-sm">Pas de données horaires</div>;

    const lastHdIdx = hourly.reduce((acc, h, i) => (h.hd ? i : acc), -1);
    const hasHd = lastHdIdx >= 0;
    const tickInterval = Math.max(0, Math.ceil(hourly.length / 8) - 1);

    return (
        <div>
            {hasHd && (
                <p className="text-[10px] text-muted-foreground mb-1.5 flex items-center gap-1.5">
                    <span className="inline-block w-2.5 h-2.5 rounded-sm bg-primary/15 border border-primary/30" />
                    Zone AROME HD (1,3 km, courte échéance) · reste : modèle global
                </p>
            )}
            <ResponsiveContainer width="100%" height={180}>
                <ComposedChart data={hourly} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} vertical={false} />
                    {hasHd && (
                        <ReferenceArea x1={hourly[0].time} x2={hourly[lastHdIdx].time} yAxisId="temp"
                            fill="currentColor" className="text-primary" fillOpacity={0.06} strokeOpacity={0} />
                    )}
                    <XAxis dataKey="time" tickFormatter={hourLabel} interval={tickInterval}
                        tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} />
                    <YAxis yAxisId="temp" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} tickLine={false}
                        tickFormatter={v => `${v}°`} width={30} />
                    <YAxis yAxisId="precip" orientation="right" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                        tickLine={false} tickFormatter={v => `${v}`} width={24} />
                    <Tooltip content={<HourlyTooltip />} />
                    <Bar yAxisId="precip" dataKey="precip" fill="#60a5fa" fillOpacity={0.7} radius={[2, 2, 0, 0]} barSize={6} />
                    <Line yAxisId="temp" type="monotone" dataKey="temp" stroke="#f97316" strokeWidth={2} dot={false} activeDot={{ r: 3 }} />
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    );
}

// ── Daily table (7 jours) ──────────────────────────────────────────────────────

function DailyTable({ daily }) {
    const { globalMin, globalMax } = useMemo(() => {
        const mins = daily.map(d => d.tmin).filter(v => v !== null && v !== undefined);
        const maxs = daily.map(d => d.tmax).filter(v => v !== null && v !== undefined);
        return {
            globalMin: mins.length ? Math.min(...mins) : 0,
            globalMax: maxs.length ? Math.max(...maxs) : 1,
        };
    }, [daily]);
    const span = Math.max(1, globalMax - globalMin);

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
                <thead>
                    <tr className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        <th className="text-left font-medium pb-1.5 pl-1">Jour</th>
                        <th className="text-left font-medium pb-1.5">Condition</th>
                        <th className="text-left font-medium pb-1.5">Pluie</th>
                        <th className="text-left font-medium pb-1.5">Vent</th>
                        <th className="text-right font-medium pb-1.5 pr-1">Min / Max</th>
                    </tr>
                </thead>
                <tbody>
                    {daily.map((d, i) => {
                        const [label, DIcon, dcls] = wmo(d.weather_code);
                        const left  = ((d.tmin - globalMin) / span) * 100;
                        const width = Math.max(6, ((d.tmax - d.tmin) / span) * 100);
                        return (
                            <tr key={i} className="border-t border-border/60">
                                <td className="py-2 pl-1 text-foreground font-medium whitespace-nowrap w-20">{dayLabel(d.date, i)}</td>
                                <td className="py-2 pr-3">
                                    <span className="flex items-center gap-1.5 whitespace-nowrap">
                                        <DIcon className={`w-4 h-4 shrink-0 ${dcls}`} />
                                        <span className="text-muted-foreground text-xs hidden sm:inline">{label}</span>
                                    </span>
                                </td>
                                <td className="py-2 pr-3 text-blue-400 text-xs whitespace-nowrap">
                                    <span className="flex items-center gap-1"><Droplets className="w-3 h-3" />{n1(d.precip_prob, '%')} · {n1d(d.precip_sum)}mm</span>
                                </td>
                                <td className="py-2 pr-3 text-muted-foreground text-xs whitespace-nowrap">
                                    <span className="flex items-center gap-1"><Wind className="w-3 h-3" />{n1(d.wind_max, ' km/h')}</span>
                                </td>
                                <td className="py-2 pr-1 w-32">
                                    <div className="flex items-center justify-end gap-2 tabular-nums text-xs">
                                        <span className="text-muted-foreground w-7 text-right">{n1(d.tmin, '°')}</span>
                                        <div className="relative h-1.5 w-16 bg-muted rounded-full overflow-hidden">
                                            <div className="absolute h-full rounded-full bg-gradient-to-r from-blue-400 to-orange-400"
                                                style={{ left: `${left}%`, width: `${width}%` }} />
                                        </div>
                                        <span className="text-foreground font-semibold w-7">{n1(d.tmax, '°')}</span>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

// ── CityPanel ──────────────────────────────────────────────────────────────────

function CityPanel({ city }) {
    if (!city.online) {
        return (
            <div className="bg-card border border-dashed border-border rounded-xl p-8 text-center">
                <CloudOff className="w-9 h-9 text-muted-foreground mx-auto mb-2" />
                <p className="font-semibold text-foreground flex items-center justify-center gap-1.5"><MapPin className="w-4 h-4" />{city.name}</p>
                <p className="text-sm text-muted-foreground mt-1">Prévisions indisponibles</p>
            </div>
        );
    }

    const c = city.current || {};
    const [label, Icon, cls] = wmo(c.weather_code);
    const today = city.daily?.[0];
    const uv = uvLevel(today?.uv_index);

    return (
        <div className="bg-card border border-border rounded-xl p-5">
            {/* Header + current */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-4">
                    <Icon className={`w-14 h-14 ${cls}`} />
                    <div>
                        <p className="font-bold text-foreground flex items-center gap-1.5"><MapPin className="w-4 h-4 text-primary" />{city.name}</p>
                        <p className="text-sm text-muted-foreground">{label}</p>
                        {today && (
                            <p className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-2">
                                <span className="flex items-center gap-0.5"><Sunrise className="w-3 h-3" />{timeLabel(today.sunrise)}</span>
                                <span className="flex items-center gap-0.5"><Sunset className="w-3 h-3" />{timeLabel(today.sunset)}</span>
                            </p>
                        )}
                    </div>
                </div>
                <div className="text-right">
                    <p className="text-4xl font-bold text-foreground tabular-nums">{n1(c.temp, '°')}</p>
                    <p className="text-xs text-muted-foreground">ressenti {n1(c.apparent, '°')}</p>
                </div>
            </div>

            {/* Current details */}
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-4">
                <StatTile icon={Droplets} label="Humidité" value={n1(c.humidity, ' %')} />
                <StatTile icon={Wind} label="Vent" value={n1(c.wind, ' km/h')} sub={c.wind_gusts ? `raf. ${n1(c.wind_gusts)}` : null} />
                <StatTile icon={Gauge} label="Pression" value={n1(c.pressure, ' hPa')} />
                <StatTile icon={CloudRain} label="Pluie" value={n1d(c.precipitation, ' mm')} />
                <StatTile icon={Thermometer} label="Ressenti" value={n1(c.apparent, '°')} />
                <div className={`rounded-lg py-2.5 px-2 text-center ${uv ? uv.cls : 'bg-muted/40'}`}>
                    <p className="text-[10px] flex items-center justify-center gap-1 opacity-80"><ShieldAlert className="w-3 h-3" />UV</p>
                    <p className="text-sm font-semibold mt-1">{n1(today?.uv_index)}</p>
                    {uv && <p className="text-[10px] mt-0.5 opacity-80">{uv.label}</p>}
                </div>
            </div>

            {/* Hourly chart — 48h */}
            <div className="mt-5">
                <p className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1.5">
                    <CloudRain className="w-3.5 h-3.5 text-blue-400" />Température &amp; pluie — 48h
                </p>
                <HourlyChart hourly={city.hourly} />
            </div>

            {/* 7-day forecast */}
            <div className="mt-4 border-t border-border pt-3">
                <p className="text-xs font-semibold text-muted-foreground mb-1">Prévisions 7 jours</p>
                <DailyTable daily={city.daily || []} />
            </div>
        </div>
    );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export function Weather() {
    const { t } = useTranslation();
    const { token } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [updatedAt, setUpdatedAt] = useState(null);

    const tokenRef = useRef(token);
    useEffect(() => { tokenRef.current = token; }, [token]);

    const fetchWeather = useCallback(async () => {
        try {
            const res = await fetch('/api/weather', { headers: { Authorization: `Bearer ${tokenRef.current}` } });
            if (res.ok) { setData(await res.json()); setUpdatedAt(new Date()); }
        } catch (e) { console.error('fetchWeather', e); }
    }, []);

    useEffect(() => {
        let mounted = true;
        fetchWeather().finally(() => { if (mounted) setLoading(false); });
        const id = setInterval(fetchWeather, POLL_MS);
        return () => { mounted = false; clearInterval(id); };
    }, [fetchWeather]);

    const manualRefresh = async () => { setRefreshing(true); await fetchWeather(); setRefreshing(false); };

    if (loading) return (
        <div className="flex items-center justify-center h-64 text-muted-foreground gap-2">
            <RefreshCw className="w-5 h-5 animate-spin" />Chargement…
        </div>
    );

    const cities = data?.cities || [];

    return (
        <div className="space-y-6 pb-10">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                        <CloudRain className="w-7 h-7 text-primary" />{t('nav.weather')}
                    </h1>
                    <p className="text-muted-foreground mt-1 text-sm">
                        Billère &amp; Lourdes
                        {updatedAt && ` · maj ${updatedAt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`}
                    </p>
                </div>
                <button onClick={manualRefresh} disabled={refreshing}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-muted text-foreground text-sm font-medium hover:bg-muted/70 disabled:opacity-50 transition-colors">
                    <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />Actualiser
                </button>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                {cities.map((city) => <CityPanel key={city.name} city={city} />)}
            </div>

            <p className="text-[11px] text-muted-foreground text-center flex items-center justify-center gap-1.5">
                <Info className="w-3 h-3" />Données : Open-Meteo — modèle global + AROME HD (Météo-France, 1,3 km) sur les prochaines 48h
            </p>
        </div>
    );
}
